/**
 * Watch Page - Stroyma
 * Movie details page with hero background and play button
 */

import { FlowcorePlayer } from '@flowcore/video-player';
import { createNavigation } from '../components/navigation.js';
import { api } from '../lib/api.js';
import { getCurrentLanguage, t } from '../lib/i18n.js';
import { isAuthenticated, login } from '../lib/keycloak.js';
import { getLocalRentalRedirect, getRentalReturnContext, resolveRentalPaymentReturn } from '../lib/rental-return.js';
import '@flowcore/video-player/styles.css';

// Store current video ID for player state
let currentWatchVideoId = null;
let isRendering = false;

const paymentText = (key, fallback) => {
  const translated = t(key);
  return translated === key ? fallback : translated;
};

function renderRentalActions({ userHasAccess, rental, rentalPrice, rentalDurationHours, paymentState, authenticated }) {
  if (userHasAccess) {
    return `
      <button id="play-btn" class="btn btn-primary btn-lg">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px;">
          <path d="M8 5v14l11-7z"/>
        </svg>
        ${t('watch_now')}
      </button>
      ${rental ? `<p style="margin-top: 1rem; color: var(--color-cloud-grey); font-size: 0.9rem;">${t('access_expires')}: ${new Date(rental.expiresAt).toLocaleString()}</p>` : ''}
    `;
  }

  if (paymentState && !paymentState.canStartCheckout) {
    if (!authenticated) {
      return `
        <div role="status" aria-live="polite">
          <p>${paymentText('watch_payment_sign_in_message', 'Sign in to confirm your rental payment.')}</p>
          <button id="payment-login-btn" class="btn btn-primary btn-lg">${paymentText('watch_payment_sign_in', 'Sign in to check payment')}</button>
        </div>
      `;
    }
    const checking = paymentState.state === 'checking';
    const message = checking
      ? paymentText('watch_payment_checking', 'Confirming your payment and rental access…')
      : paymentState.state === 'error'
        ? paymentText('watch_payment_check_failed', 'We could not confirm your payment yet. Check its status again.')
        : paymentText(
            'watch_payment_pending',
            'Your payment is still being confirmed. Check its status again in a moment.',
          );
    return `
      <div role="status" aria-live="polite" aria-busy="${checking}">
        <p>${message}</p>
        <button id="payment-check-btn" class="btn btn-secondary" ${checking ? 'disabled' : ''}>${paymentText('watch_payment_check_again', 'Check payment status')}</button>
        ${paymentState.canResumeCheckout ? `<button id="resume-payment-btn" class="btn btn-primary">${paymentText('watch_payment_resume', 'Resume payment')}</button>` : ''}
      </div>
    `;
  }

  let paymentMessage = '';
  if (paymentState?.state === 'unpaid') {
    paymentMessage = paymentText('watch_payment_unpaid', 'The payment did not complete. You can try renting again.');
  } else if (paymentState?.state === 'refunded') {
    paymentMessage = paymentText('watch_payment_refunded', 'This rental payment was refunded.');
  } else if (paymentState?.state === 'expired') {
    paymentMessage = paymentText('watch_payment_expired', 'This rental has expired.');
  }
  return `
    ${paymentMessage ? `<p role="status">${paymentMessage}</p>` : ''}
    <button id="rent-btn" class="btn btn-primary btn-lg">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px;">
        <path d="M21 12h-2v2h-2v-2h-2v-2h2V8h2v2h2v2zM7 12c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 8c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
      </svg>
      ${t('rent_for')} ${(rentalPrice / 100).toFixed(0)} ${t('rent_currency')}
    </button>
    <p style="margin-top: 1rem; color: var(--color-cloud-grey); font-size: 0.9rem;">${t('hour_access').replace('{hours}', rentalDurationHours)}</p>
  `;
}

export async function renderWatch(params) {
  // Prevent duplicate rendering
  if (isRendering) {
    console.log('⚠️ renderWatch already in progress, skipping duplicate call');
    return;
  }

  isRendering = true;

  const app = document.getElementById('app');

  try {
    // Clear app and remove any existing watch sections
    app.innerHTML = '';

    // Remove any existing watch-hero sections (in case of duplicate)
    const existingWatchSections = document.querySelectorAll('.watch-hero');
    existingWatchSections.forEach((section) => {
      section.remove();
    });

    // Navigation
    app.appendChild(await createNavigation());

    const videoId = params.id;
    currentWatchVideoId = videoId; // Store for player navigation

    if (!videoId) {
      app.innerHTML +=
        '<div class="container" style="padding-top: 100px;"><p class="text-center">Video not found.</p></div>';
      isRendering = false;
      return;
    }

    // Check for placeholder mode
    const urlParams = new URLSearchParams(window.location.search);
    const showPlaceholders = urlParams.get('placeholder') === 'true';
    const paymentReturn = getRentalReturnContext(window.location.search);
    // Fetch video data
    const video = await api.getVideo(videoId);
    const currentLang = getCurrentLanguage();

    // Helper to format person name
    const formatPersonName = (person) => {
      if (!person) return '';
      if (typeof person === 'string') return person;
      const parts = [person.firstName, person.middleName, person.lastName].filter((p) => p);
      return parts.join(' ');
    };

    // Helper to format array of people
    const formatPeopleList = (people) => {
      if (!people) return '';
      if (typeof people === 'string') return people;
      if (Array.isArray(people)) {
        return people
          .map((p) => formatPersonName(p))
          .filter((n) => n)
          .join(', ');
      }
      return formatPersonName(people);
    };

    // Helper function to get placeholder or actual value
    const getValue = (actualValue, placeholderText) => {
      if (showPlaceholders && !actualValue) {
        return `<span style="color: var(--color-cloud-grey); font-style: italic; opacity: 0.7;">${placeholderText}</span>`;
      }
      return actualValue || '';
    };

    // Helper function to get placeholder or actual value for person names
    const _getPersonValue = (person, placeholderText) => {
      if (showPlaceholders && !person) {
        return `<span style="color: var(--color-cloud-grey); font-style: italic; opacity: 0.7;">${placeholderText}</span>`;
      }
      const name = formatPersonName(person);
      return name || '';
    };

    // Helper function to get placeholder or actual value for people lists
    const getPeopleListValue = (people, placeholderText) => {
      if (showPlaceholders && (!people || (Array.isArray(people) && people.length === 0))) {
        return `<span style="color: var(--color-cloud-grey); font-style: italic; opacity: 0.7;">${placeholderText}</span>`;
      }
      const list = formatPeopleList(people);
      return list || '';
    };

    // Get title based on language
    const originalTitle = video.originalTitle || video.metadata?.originalTitle;
    const internationalTitle = video.internationalTitle || video.metadata?.internationalTitle;
    let title = currentLang === 'fo' ? originalTitle || internationalTitle : internationalTitle || originalTitle;
    if (showPlaceholders && !title) {
      title = currentLang === 'fo' ? 'Føroyskt heiti / Faroese Title' : 'International Title';
    }

    // Get logline based on language
    const loglineFo = video.loglineFo || video.metadata?.loglineFo || '';
    const loglineEn = video.loglineEn || video.metadata?.loglineEn || '';
    let logline = currentLang === 'fo' ? loglineFo || loglineEn : loglineEn || loglineFo;
    // Ensure logline is not empty string and is different from synopsis
    if (!logline || logline.trim() === '') {
      logline = null; // Set to null so it won't display
    }
    if (showPlaceholders && !logline) {
      logline = currentLang === 'fo' ? 'Stuttur søgusamandráttur / Logline' : 'Brief logline about the film';
    }

    // Get synopsis based on language
    const synopsisFo = video.synopsisFo || video.metadata?.synopsisFo || '';
    const synopsisEn = video.synopsisEn || video.metadata?.synopsisEn || '';
    let synopsis = currentLang === 'fo' ? synopsisFo || synopsisEn : synopsisEn || synopsisFo;
    // Ensure synopsis is not empty string
    if (!synopsis || synopsis.trim() === '') {
      synopsis = null;
    }
    if (showPlaceholders && !synopsis) {
      synopsis =
        currentLang === 'fo'
          ? 'Stuttur søgusamandráttur um filmin. Her skal koma ein stuttur tekstur, ið greiðir frá, hvat filmin handlar um.'
          : 'Brief synopsis about the film. A short text describing what the film is about.';
    }

    // Prevent logline from showing synopsis - if they're the same, hide logline
    if (logline && synopsis && logline.trim() === synopsis.trim()) {
      logline = null;
    }

    // Get runtime
    const runtimeHours = video.runtimeHours || video.metadata?.runtimeHours || 0;
    const runtimeMinutes = video.runtimeMinutes || video.metadata?.runtimeMinutes || 0;
    let runtimeText = runtimeHours > 0 ? `${runtimeHours}h ${runtimeMinutes}m` : `${runtimeMinutes}m`;
    if (showPlaceholders && runtimeHours === 0 && runtimeMinutes === 0) {
      runtimeText = '1h 30m';
    }

    // Get year
    let year = video.productionYear || video.metadata?.productionYear || video.year;
    if (showPlaceholders && !year) {
      year = new Date().getFullYear();
    }

    // Get category (singular form for individual film labels)
    const category = video.category || video.metadata?.category;
    const categoryName = category ? t(`category_singular_${category}`) : '';

    // Get still or poster for background. Resolve relative /images/asset/*
    // URLs against the API base (see lib/api.js#resolveAsset).
    let backgroundUrl =
      api.resolveImageAsset(
        video.stillUrl || video.metadata?.stillUrl || video.posterUrl || video.metadata?.posterUrl,
        {
          w: 1280,
          fit: 'inside',
          q: 80,
          fmt: 'webp',
        },
      ) || '';
    if (showPlaceholders && !backgroundUrl) {
      backgroundUrl =
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkyMCIgaGVpZ2h0PSIxMDgwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiMxRDFGNjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjI0IiBmaWxsPSIjNkU3NjgxIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UGxhY2Vob2xkZXI8L3RleHQ+PC9zdmc+';
    }

    // Get rental info
    const rentalPrice = video.rentalPrice || video.metadata?.rentalPrice || 0;
    const rentalDurationHours = video.rentalDurationHours || video.metadata?.rentalDurationHours || 48;
    const hasRentalSettings = rentalPrice > 0;
    const imdbLink = (video.imdbLink || video.metadata?.imdbLink || '').trim();

    // Check if user has access (if logged in and has rental settings)
    let userHasAccess = false;
    let rental = null;
    let paymentReturnState = paymentReturn ? { state: 'checking', canStartCheckout: false } : null;
    if (isAuthenticated() && hasRentalSettings && !paymentReturn) {
      // API client now automatically handles token refresh
      try {
        const accessResult = await api.checkVideoAccess(videoId);
        userHasAccess = accessResult.hasAccess;
        rental = accessResult.rental;
      } catch (err) {
        console.error('Failed to check access:', err);
      }
    }

    // Create hero section with background
    const watchSection = document.createElement('section');
    watchSection.className = 'watch-hero';
    watchSection.innerHTML = `
      <!-- Background Image -->
      <div class="watch-hero-background">
        <img src="${backgroundUrl}" alt="${title || 'Video'}" onerror="this.style.display='none'">
        <div class="watch-hero-gradient"></div>
      </div>
      
      <!-- Content -->
      <div class="watch-hero-content">
        <div class="container">
          <div class="watch-hero-layout">
            <!-- Poster -->
            ${
              video.posterUrl || video.metadata?.posterUrl || showPlaceholders
                ? `
              <div class="watch-poster">
                ${
                  video.posterUrl || video.metadata?.posterUrl
                    ? `
                <img src="${api.resolveImageAsset(video.posterUrl || video.metadata?.posterUrl, { w: 640, fit: 'inside', q: 80, fmt: 'webp' })}" alt="${title}" loading="eager">
                `
                    : showPlaceholders
                      ? `
                  <div style="width: 100%; height: 100%; background: var(--color-storm-grey); display: flex; align-items: center; justify-content: center; color: var(--color-cloud-grey); font-style: italic; opacity: 0.7;">
                    Poster Placeholder<br><small style="font-size: 0.75rem;">2:3 aspect ratio</small>
                  </div>
                `
                      : ''
                }
              </div>
            `
                : ''
            }
            
            <!-- Info -->
            <div class="watch-info">
              <!-- Title & Badges -->
              <div class="watch-badges">
                ${video.isFaroese || video.metadata?.isFaroese ? '<span class="badge badge-original">Faroese Original</span>' : ''}
                ${categoryName ? `<span class="badge">${categoryName}</span>` : ''}
                ${video.ageRating ? `<span class="age-badge age-badge--${video.ageRating}" title="${{ A: 'Hóskandi til øll', 7: 'Frámælt børnum undir 7 ár', 11: 'Hóskandi til børn 11 ár og eldri', 15: 'Hóskandi til børn 15 ár og eldri' }[video.ageRating] || ''}" style="display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.75rem; font-weight: 700; line-height: 1.4; color: #fff; cursor: help; background: ${video.ageRating === 'A' ? '#2ea043' : video.ageRating === '7' ? '#d4a72c' : video.ageRating === '11' ? '#d18616' : '#da3633'};">${video.ageRating === 'A' ? 'A' : `${video.ageRating}+`}</span>` : ''}
              </div>
              
              <h1 class="watch-title">${title || 'Untitled'}</h1>
              
              <!-- Meta Info -->
              <div class="watch-meta">
                ${year ? `<span>${year}</span>` : ''}
                ${runtimeHours > 0 || runtimeMinutes > 0 || showPlaceholders ? `<span>${runtimeText}</span>` : ''}
              </div>
              
              <!-- Logline (short teaser in hero) -->
              ${logline ? `<p class="watch-logline">${logline}</p>` : ''}
              
              <!-- Play/Rent Button -->
              <div class="watch-actions">
                ${
                  video.geoBlocked
                    ? `
                  <div style="display: flex; align-items: center; gap: 0.75rem; padding: 1rem 1.25rem; background: rgba(218, 54, 51, 0.15); border: 1px solid rgba(218, 54, 51, 0.3); border-radius: 8px;">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#da3633" stroke-width="1.5" style="flex-shrink: 0;">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M4.93 4.93l14.14 14.14"/>
                    </svg>
                    <div>
                      <p style="margin: 0; color: #da3633; font-weight: 600;">${t('watch_geo_blocked_title')}</p>
                      <p style="margin: 0.25rem 0 0; color: var(--color-cloud-grey); font-size: 0.85rem;">${t('watch_geo_blocked_message')}</p>
                    </div>
                  </div>
                `
                    : hasRentalSettings
                      ? renderRentalActions({
                          userHasAccess,
                          rental,
                          rentalPrice,
                          rentalDurationHours,
                          paymentState: paymentReturnState,
                          authenticated: isAuthenticated(),
                        })
                      : `
                  <button id="play-btn" class="btn btn-primary btn-lg">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px;">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                    ${t('watch_now') || 'Watch Now'}
                  </button>
                `
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Details Section -->
      <div class="watch-details">
        <div class="container">
          <div class="watch-details-grid">

            <div class="watch-details-main">
              <!-- Synopsis (full story in details section only) -->
              ${
                synopsis || showPlaceholders
                  ? `
                <div class="watch-details-section">
                  <h3>${t('watch_synopsis')}</h3>
                  <p>${synopsis}</p>
                </div>
              `
                  : ''
              }

              
              ${(() => {
                const screenings = video.screenings || video.metadata?.screenings || [];
                if (screenings.length === 0) return '';
                return `
                <div class="watch-details-section">
                  <h3>${t('watch_screenings')}</h3>
                  <ul style="list-style: none; padding: 0; margin: 0;">
                    ${screenings
                      .map(
                        (s) => `
                      <li style="margin-bottom: 0.5rem;">
                        <strong>${s.festival || ''}</strong>${s.location ? ` — ${s.location}` : ''}${s.year ? ` (${s.year})` : ''}${s.awards ? `<br><span style="color: var(--color-arctic-white);">🏆 ${s.awards}</span>` : ''}
                      </li>
                    `,
                      )
                      .join('')}
                  </ul>
                </div>`;
              })()}

              ${
                imdbLink
                  ? `
                <div class="watch-details-section">
                  <a href="${imdbLink}" target="_blank" rel="noopener noreferrer" style="color: var(--color-nordic-blue); text-decoration: underline;">${t('watch_read_more_imdb')} →</a>
                </div>
              `
                  : ''
              }

              ${
                video.mainCast || video.metadata?.mainCast || showPlaceholders
                  ? `
                <div class="watch-details-section">
                  <h3>${t('watch_cast')}</h3>
                  <p>${getPeopleListValue(video.mainCast || video.metadata?.mainCast, currentLang === 'fo' ? 'Høvuðsleikarar / Main Cast' : 'Main Cast')}</p>
                </div>
              `
                  : ''
              }
              
            </div>
            
            <!-- Metadata -->
            <div class="watch-details-sidebar">
              ${
                video.director || video.metadata?.director || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_director')}:</span>
                  <span>${(() => {
                    const directorData = video.director || video.metadata?.director;
                    const directors = Array.isArray(directorData) ? directorData : directorData ? [directorData] : [];
                    const links = directors
                      .map((d) => {
                        const name = formatPersonName(d);
                        if (name)
                          return `<a href="/search?q=${encodeURIComponent(name)}" data-link style="color: var(--color-nordic-blue); text-decoration: underline;">${name}</a>`;
                        return '';
                      })
                      .filter((l) => l);
                    if (links.length > 0) return links.join(', ');
                    if (showPlaceholders) {
                      return `<span style="color: var(--color-cloud-grey); font-style: italic; opacity: 0.7;">${currentLang === 'fo' ? 'Leikstjóri / Director' : 'Director'}</span>`;
                    }
                    return '';
                  })()}</span>
                </div>
              `
                  : ''
              }

              ${
                video.screenwriter || video.metadata?.screenwriter || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_screenwriter')}:</span>
                  <span>${getPeopleListValue(video.screenwriter || video.metadata?.screenwriter, currentLang === 'fo' ? 'Handritshøvundur / Screenwriter' : 'Screenwriter')}</span>
                </div>
              `
                  : ''
              }

              ${
                video.producer || video.metadata?.producer || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_producer')}:</span>
                  <span>${getPeopleListValue(video.producer || video.metadata?.producer, currentLang === 'fo' ? 'Framleiðari / Producer' : 'Producer')}</span>
                </div>
              `
                  : ''
              }

              ${
                video.dop || video.metadata?.dop || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_cinematographer')}:</span>
                  <span>${getPeopleListValue(video.dop || video.metadata?.dop, currentLang === 'fo' ? 'Fotograf / Cinematographer' : 'Cinematographer')}</span>
                </div>
              `
                  : ''
              }

              ${
                video.editor || video.metadata?.editor || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_editor')}:</span>
                  <span>${getPeopleListValue(video.editor || video.metadata?.editor, currentLang === 'fo' ? 'Klippari / Editor' : 'Editor')}</span>
                </div>
              `
                  : ''
              }

              ${
                video.composer || video.metadata?.composer || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_composer')}:</span>
                  <span>${getPeopleListValue(video.composer || video.metadata?.composer, currentLang === 'fo' ? 'Tónaskald / Composer' : 'Composer')}</span>
                </div>
              `
                  : ''
              }
              
              ${
                video.productionCompany || video.metadata?.productionCompany || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_production')}:</span>
                  <span>${getValue(video.productionCompany || video.metadata?.productionCompany, currentLang === 'fo' ? 'Framleiðslufelag / Production Company' : 'Production Company')}</span>
                </div>
              `
                  : ''
              }

              ${(() => {
                const keyCrew = video.keyCrew || video.metadata?.keyCrew || [];
                if (keyCrew.length === 0 && !showPlaceholders) return '';
                if (keyCrew.length === 0 && showPlaceholders) {
                  return `<div class="watch-details-item">
                    <span class="watch-details-label">${t('watch_key_crew')}:</span>
                    <span style="color: var(--color-cloud-grey); font-style: italic; opacity: 0.7;">${currentLang === 'fo' ? 'Lyklapersónar' : 'Key Crew'}</span>
                  </div>`;
                }
                return keyCrew
                  .map((member) => {
                    const name = formatPersonName(member);
                    if (!name) return '';
                    const roleLabel = member.role ? t(`role_${member.role}`) || member.role : t('watch_key_crew');
                    return `<div class="watch-details-item">
                    <span class="watch-details-label">${roleLabel}:</span>
                    <span>${name}</span>
                  </div>`;
                  })
                  .join('');
              })()}

              ${
                video.language || video.metadata?.language || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_language')}:</span>
                  <span>${getValue(video.language || video.metadata?.language, currentLang === 'fo' ? 'Mál / Language' : 'Language')}</span>
                </div>
              `
                  : ''
              }
              
              ${
                video.subtitles || video.metadata?.subtitles || showPlaceholders
                  ? `
                <div class="watch-details-item">
                  <span class="watch-details-label">${t('watch_subtitles')}:</span>
                  <span>${getValue(video.subtitles || video.metadata?.subtitles, currentLang === 'fo' ? 'Undirtekstir / Subtitles' : 'Subtitles')}</span>
                </div>
              `
                  : ''
              }
            </div>
          </div>
          
          <!-- Back Button -->

          <div class="watch-back">
            <a href="/" class="btn btn-secondary" data-link>← ${t('watch_back')}</a>
          </div>
        </div>
      </div>
    `;

    // Remove any existing watch sections before appending (prevent duplicates)
    const existingSections = app.querySelectorAll('.watch-hero');
    existingSections.forEach((section) => {
      section.remove();
    });

    app.appendChild(watchSection);

    const bindWatchActions = () => {
      // Add event listener to play button
      const playBtn = watchSection.querySelector('#play-btn');
      if (playBtn) {
        playBtn.addEventListener('click', () => {
          // Free videos (no rental settings) can be played without authentication
          // Paid videos require authentication and rental access check
          if (!hasRentalSettings) {
            // Free video - play without authentication
            openPlayer(videoId, title);
          } else if (!isAuthenticated()) {
            // Paid video - require authentication
            sessionStorage.setItem('intended_path', window.location.pathname + window.location.search);
            login();
          } else {
            // Authenticated user with paid video
            openPlayer(videoId, title);
          }
        });
      }

      // Add event listener to rent button
      const rentBtn = watchSection.querySelector('#rent-btn, #resume-payment-btn');
      if (rentBtn) {
        rentBtn.addEventListener('click', async () => {
          // Check authentication before renting
          if (!isAuthenticated()) {
            // Save intended path and redirect to login
            sessionStorage.setItem('intended_path', window.location.pathname + window.location.search);
            login();
            return;
          }

          const originalButtonContent = rentBtn.innerHTML;
          const resumeAttemptId = rentBtn.id === 'resume-payment-btn' ? paymentReturn?.attemptId : undefined;
          // Disable button and show loading
          rentBtn.disabled = true;
          rentBtn.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px; animation: spin 1s linear infinite;">
            <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
          </svg>
          Processing...
        `;

          try {
            // API client now automatically handles token refresh
            // Create checkout session
            const result = await api.createRentalCheckout(videoId, resumeAttemptId);

            if (result.devMode) {
              // Dev mode: free rental created, reload page
              alert('Rental activated (dev mode)');
              window.location.reload();
            } else if (result.redirectUrl) {
              // Existing access or a reconciled attempt needs no new charge.
              window.location.href = getLocalRentalRedirect(result, videoId);
            } else if (result.checkoutUrl) {
              // Redirect to the selected payment provider's hosted checkout
              window.location.href = result.checkoutUrl;
            } else {
              throw new Error('No checkout URL returned');
            }
          } catch (error) {
            console.error('Rental error:', error);
            // Check if it's an authentication error
            if (error.message?.includes('401')) {
              alert('Your session has expired. Please log in again.');
              sessionStorage.setItem('intended_path', window.location.pathname + window.location.search);
              login();
              return;
            }
            alert('Failed to start rental process. Please try again.');
            rentBtn.disabled = false;
            rentBtn.innerHTML = originalButtonContent;
          }
        });
      }

      const paymentLoginBtn = watchSection.querySelector('#payment-login-btn');
      if (paymentLoginBtn) {
        paymentLoginBtn.addEventListener('click', () => {
          // Keep the attempt reference across authentication so a paid return
          // cannot become a fresh checkout when the session has expired.
          sessionStorage.setItem('intended_path', window.location.pathname + window.location.search);
          login();
        });
      }

      const paymentCheckBtn = watchSection.querySelector('#payment-check-btn');
      if (paymentCheckBtn) paymentCheckBtn.addEventListener('click', () => window.location.reload());
    };

    bindWatchActions();

    if (paymentReturn && hasRentalSettings && !video.geoBlocked && isAuthenticated()) {
      // The pending panel is already visible. Do not hold up page rendering
      // while webhooks or the payment provider finish processing the charge.
      void resolveRentalPaymentReturn({
        videoId,
        context: paymentReturn,
        // Status may make multiple provider calls with 15-second deadlines;
        // allow them to finish while keeping the whole polling cycle bounded.
        paymentRequestTimeoutMs: 35_000,
        maxDurationMs: 45_000,
        reconcilePayment: (attemptId, checkedVideoId, signal) =>
          api.reconcileRentalPayment(attemptId, checkedVideoId, { signal }),
        checkAccess: (checkedVideoId, signal) => api.checkVideoAccess(checkedVideoId, { throwOnError: true, signal }),
      }).then((result) => {
        if (!watchSection.isConnected || currentWatchVideoId !== videoId) return;

        paymentReturnState = result;
        userHasAccess = result.state === 'access';
        rental = result.rental || null;

        if (userHasAccess) {
          const confirmedUrl = new URL(window.location.href);
          confirmedUrl.searchParams.delete('payment');
          confirmedUrl.searchParams.delete('rental_attempt');
          window.history.replaceState({}, '', confirmedUrl.pathname + confirmedUrl.search + confirmedUrl.hash);
        }

        const actions = watchSection.querySelector('.watch-actions');
        actions.innerHTML = renderRentalActions({
          userHasAccess,
          rental,
          rentalPrice,
          rentalDurationHours,
          paymentState: paymentReturnState,
          authenticated: isAuthenticated(),
        });
        bindWatchActions();
      });
    }

    // Reset rendering flag on successful completion
    isRendering = false;
  } catch (error) {
    console.error('Failed to load video:', error);
    if (app) {
      const errorSection = document.createElement('section');
      errorSection.className = 'section';
      errorSection.style.paddingTop = '100px';
      errorSection.innerHTML = `
        <div class="container">
          <div style="text-align: center; padding: var(--spacing-2xl);">
            <h2 style="margin-bottom: var(--spacing-md);">${t('error') || 'Unable to Load Video'}</h2>
            <p style="color: var(--color-cloud-grey); margin-bottom: var(--spacing-xl);">
              ${t('error_video_load') || "The video you're looking for could not be loaded."}
            </p>
            <a href="/" class="btn btn-primary" data-link>${t('nav_home') || 'Home'}</a>
          </div>
        </div>
      `;
      app.appendChild(errorSection);
    }

    // Reset rendering flag on error
    isRendering = false;
  }
}

// Open fullscreen player
async function openPlayer(videoId, _title) {
  const app = document.getElementById('app');

  // No history entry for the player — back button should navigate away from the watch page.
  // The player can be closed via ESC key or the close button.

  // Replace entire app content with fullscreen player (with loading state)
  app.innerHTML = `
    <div class="fullscreen-player-page">
      <div class="player-controls-zone">
        <button id="exit-player-btn" class="exit-player-btn" aria-label="Exit player">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Back</span>
        </button>
      </div>
      <div id="video-player-container" class="video-player-container">
        <div class="loading-container">
          <div class="spinner"></div>
        </div>
      </div>
      <div id="resume-prompt" style="display: none;"></div>
    </div>
  `;

  // Setup exit button immediately
  const exitBtn = document.getElementById('exit-player-btn');
  if (exitBtn) {
    exitBtn.addEventListener('click', () => {
      exitPlayer();
    });
  }

  try {
    // Check for saved watch progress if user is logged in
    let savedProgress = null;
    if (isAuthenticated()) {
      try {
        const progressData = await api.getWatchProgress(videoId);
        if (progressData?.progress && progressData.progress.positionSeconds > 10) {
          savedProgress = progressData.progress;
        }
      } catch (_err) {
        console.log('No saved progress found');
      }
    }

    // Fetch the actual HLS manifest URL from the API
    const streamEndpoint = api.getStreamUrl(videoId);

    // Include auth token if available
    const token = localStorage.getItem('keycloak_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const streamResponse = await fetch(streamEndpoint, { headers });

    if (!streamResponse.ok) {
      // Handle geo-blocking 403
      if (streamResponse.status === 403) {
        try {
          const errorData = await streamResponse.json();
          if (errorData.error === 'geo_blocked') {
            const playerElement = document.getElementById('video-player-container');
            if (playerElement) {
              playerElement.innerHTML = `
                <div style="
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  height: 100%;
                  min-height: 300px;
                  text-align: center;
                  color: white;
                  padding: 2rem;
                  background: rgba(13, 17, 23, 0.95);
                  border-radius: 12px;
                ">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 1rem; opacity: 0.7;">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M4.93 4.93l14.14 14.14"/>
                  </svg>
                  <h2 style="margin: 0 0 0.5rem; font-size: 1.5rem;">${t('watch_geo_blocked_title')}</h2>
                  <p style="margin: 0; color: var(--color-mist-grey, #999); max-width: 400px; line-height: 1.6;">${t('watch_geo_blocked_message')}</p>
                </div>
              `;
            }
            return;
          }
        } catch (_e) {
          // Not JSON, fall through to generic error
        }
      }
      const errorText = await streamResponse.text();
      throw new Error(errorText || 'Failed to get stream URL');
    }

    const streamData = await streamResponse.json();
    // Relative proxy URLs need the API base URL prepended
    const manifestUrl = streamData.manifestUrl.startsWith('/')
      ? `${api.baseUrl}${streamData.manifestUrl}`
      : streamData.manifestUrl;

    // Fetch subtitles for this video
    let subtitles = [];
    try {
      const subtitlesResponse = await fetch(`${api.baseUrl}/videos/${videoId}/subtitles`, { headers });
      if (subtitlesResponse.ok) {
        const subtitlesData = await subtitlesResponse.json();
        // Use proxy URL for all environments to avoid CORS issues
        // The proxy endpoint handles CORS headers properly
        subtitles = (subtitlesData.subtitles || []).map((s) => ({
          src: `${api.baseUrl}/subtitles/proxy/${videoId}/${s.id}`, // Use proxy for all environments
          srclang: s.language,
          label: s.label,
          default: false, // Can be enhanced to set default based on user preference
        }));
      }
    } catch (subtitleError) {
      console.warn('Failed to fetch subtitles:', subtitleError);
      // Continue without subtitles
    }

    // Initialize Flowcore Player with custom controls
    const playerElement = document.getElementById('video-player-container');

    if (playerElement) {
      // Clear loading state
      playerElement.innerHTML = '';

      // Initialize FlowcorePlayer
      const player = new FlowcorePlayer({
        container: playerElement,
        streamUrl: manifestUrl,
        autoplay: !savedProgress, // Don't autoplay if we have resume prompt
        controls: true,
        subtitles: subtitles.length > 0 ? subtitles : undefined,
        videoId: videoId, // Pass videoId for subtitle preference storage
        onEnded: () => {
          exitPlayer();
        },
      });

      // Store player instance for cleanup
      window.currentPlayer = player;
      window.currentVideoId = videoId;

      // Show resume prompt if we have saved progress
      if (savedProgress) {
        showResumePrompt(savedProgress.positionSeconds, player);
      } else {
        // Start tracking immediately if no resume needed
        initializeVideoTracking(player, videoId);
      }

      // ESC key to exit
      const escHandler = (e) => {
        if (e.key === 'Escape') {
          exitPlayer();
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);
      window.playerEscHandler = escHandler;
    }
  } catch (error) {
    console.error('Failed to load video:', error);
    const playerElement = document.getElementById('video-player-container');
    if (playerElement) {
      playerElement.innerHTML = `
        <div style="text-align: center; color: white;">
          <p>Failed to load video</p>
          <button onclick="window.location.reload()" class="btn btn-primary">Retry</button>
        </div>
      `;
    }
  }
}

// Show resume playback prompt
function showResumePrompt(positionSeconds, player) {
  const resumePrompt = document.getElementById('resume-prompt');
  if (!resumePrompt) return;

  const minutes = Math.floor(positionSeconds / 60);
  const seconds = Math.floor(positionSeconds % 60);
  const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  resumePrompt.style.display = 'flex';
  resumePrompt.innerHTML = `
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(13, 17, 23, 0.95);
      padding: 2rem;
      border-radius: 12px;
      text-align: center;
      z-index: 1000;
      min-width: 300px;
    ">
      <h3 style="color: white; margin-bottom: 1rem;">Resume Playback?</h3>
      <p style="color: #E1E4E8; margin-bottom: 1.5rem;">Continue from ${timeString}</p>
      <div style="display: flex; gap: 1rem; justify-content: center;">
        <button id="resume-btn" class="btn btn-primary">Resume</button>
        <button id="restart-btn" class="btn btn-secondary">Start Over</button>
      </div>
    </div>
  `;

  document.getElementById('resume-btn').addEventListener('click', () => {
    player.seek(positionSeconds);
    player.play();
    resumePrompt.style.display = 'none';
    initializeVideoTracking(player, window.currentVideoId, false); // false = don't count as new view
  });

  document.getElementById('restart-btn').addEventListener('click', () => {
    player.seek(0);
    player.play();
    resumePrompt.style.display = 'none';
    initializeVideoTracking(player, window.currentVideoId, true); // true = count as new view
  });
}

// Initialize video tracking (view counting, watch time, progress saving)
function initializeVideoTracking(player, videoId, countAsNewView = true) {
  if (!isAuthenticated()) return; // Only track for logged-in users

  let viewStartRecorded = false;
  let lastSavedPosition = 0;
  let watchStartTime = Date.now();
  let _totalWatchTime = 0;
  let isPlaying = false;

  // Record view start
  if (countAsNewView) {
    api.recordAnalyticsEvent(videoId, 'view_start').then(() => {
      console.log('[Analytics] View started');
      viewStartRecorded = true;
    });
  }

  // Save progress every 10 seconds while playing
  const progressInterval = setInterval(async () => {
    if (!isPlaying || !player?.getCurrentTime) return;

    try {
      const currentTime = player.getCurrentTime();
      const duration = player.getDuration();

      // Only save if position changed significantly (more than 2 seconds)
      if (Math.abs(currentTime - lastSavedPosition) > 2) {
        const completed = duration > 0 && currentTime / duration > 0.9;

        await api.saveWatchProgress(videoId, currentTime, duration, completed);
        lastSavedPosition = currentTime;

        if (completed) {
          // Record completion event
          await api.recordAnalyticsEvent(videoId, 'completion', {
            positionSeconds: currentTime,
            durationSeconds: duration,
          });
          console.log('[Analytics] Video completed');
        }
      }
    } catch (err) {
      console.error('[Analytics] Failed to save progress:', err);
    }
  }, 10000); // Every 10 seconds

  // Track watch time every 30 seconds
  const watchTimeInterval = setInterval(async () => {
    if (!isPlaying) return;

    const now = Date.now();
    const sessionWatchTime = (now - watchStartTime) / 1000;
    _totalWatchTime += sessionWatchTime;
    watchStartTime = now;

    try {
      await api.recordAnalyticsEvent(videoId, 'watch_time', {
        durationSeconds: sessionWatchTime,
      });
      console.log('[Analytics] Watch time recorded:', Math.floor(sessionWatchTime), 's');
    } catch (err) {
      console.error('[Analytics] Failed to record watch time:', err);
    }
  }, 30000); // Every 30 seconds

  // Listen to player events
  try {
    // Play event
    if (player.on) {
      player.on('play', () => {
        isPlaying = true;
        watchStartTime = Date.now();
      });

      player.on('pause', () => {
        isPlaying = false;
        // Save progress immediately on pause
        if (player.getCurrentTime) {
          const currentTime = player.getCurrentTime();
          const duration = player.getDuration();
          api.saveWatchProgress(videoId, currentTime, duration, false);
        }
      });

      player.on('ended', async () => {
        isPlaying = false;
        // Mark as completed
        if (player.getCurrentTime && player.getDuration) {
          const currentTime = player.getCurrentTime();
          const duration = player.getDuration();
          await api.saveWatchProgress(videoId, currentTime, duration, true);
          await api.recordAnalyticsEvent(videoId, 'completion', {
            positionSeconds: currentTime,
            durationSeconds: duration,
          });
        }
      });
    }
  } catch (err) {
    console.warn('[Analytics] Player events not available:', err);
  }

  // Cleanup on exit
  window.trackingCleanup = () => {
    clearInterval(progressInterval);
    clearInterval(watchTimeInterval);

    // Final save on exit
    if (player?.getCurrentTime) {
      try {
        const currentTime = player.getCurrentTime();
        const duration = player.getDuration();
        api.saveWatchProgress(videoId, currentTime, duration, false);

        // Record view end
        if (viewStartRecorded) {
          api.recordAnalyticsEvent(videoId, 'view_end', {
            positionSeconds: currentTime,
            durationSeconds: duration,
          });
        }
      } catch (err) {
        console.error('[Analytics] Failed final save:', err);
      }
    }
  };
}

// Exit player and restore content
async function exitPlayer() {
  // Run tracking cleanup before destroying player
  if (window.trackingCleanup) {
    try {
      window.trackingCleanup();
      window.trackingCleanup = null;
    } catch (e) {
      console.warn('Failed to run tracking cleanup:', e);
    }
  }

  if (window.currentPlayer) {
    try {
      if (window.currentPlayer.destroy) {
        window.currentPlayer.destroy();
      }
    } catch (e) {
      console.warn('Failed to destroy player:', e);
    }
    window.currentPlayer = null;
  }

  // Clear video ID
  window.currentVideoId = null;

  // Remove ESC handler
  if (window.playerEscHandler) {
    document.removeEventListener('keydown', window.playerEscHandler);
    window.playerEscHandler = null;
  }

  // Re-render the watch page
  if (currentWatchVideoId) {
    await renderWatch({ id: currentWatchVideoId });
  }
}

// Handle browser back button — if the player is open, clean it up
// (the router's popstate handler will navigate to the previous page)
window.addEventListener('popstate', () => {
  if (window.currentVideoId && document.querySelector('.fullscreen-player-page')) {
    // Clean up player resources without re-rendering (we're navigating away)
    try {
      if (window.currentPlayer) {
        window.currentPlayer.destroy();
      }
    } catch (_e) {
      /* ignore */
    }
    window.currentPlayer = null;
    window.currentVideoId = null;
    if (window.playerEscHandler) {
      document.removeEventListener('keydown', window.playerEscHandler);
      window.playerEscHandler = null;
    }
  }
});

function _applyPlayerStyles() {
  const style = document.createElement('style');
  style.id = 'stroyma-player-styles';
  style.textContent = `
    /* Stroyma Player Customization */
    .flowcore-player {
      font-family: var(--font-secondary);
      border-radius: var(--radius-md);
    }
    
    .flowcore-player .controls {
      background: linear-gradient(180deg, transparent 0%, rgba(13, 17, 23, 0.95) 100%);
    }
    
    .flowcore-player .play-button,
    .flowcore-player .control-button {
      color: var(--color-arctic-white);
      transition: color 0.2s ease;
    }
    
    .flowcore-player .play-button:hover,
    .flowcore-player .control-button:hover {
      color: var(--color-nordic-blue);
    }
    
    .flowcore-player .progress-bar {
      background: rgba(225, 228, 232, 0.3);
    }
    
    .flowcore-player .progress-filled {
      background: var(--color-nordic-blue);
    }
    
    .flowcore-player .volume-slider::-webkit-slider-thumb {
      background: var(--color-nordic-blue);
    }
    
    .flowcore-player .volume-slider::-moz-range-thumb {
      background: var(--color-nordic-blue);
    }
  `;

  const existing = document.getElementById('stroyma-player-styles');
  if (existing) {
    existing.remove();
  }

  document.head.appendChild(style);
}

// Cleanup on page change
export function cleanup() {
  if (window.currentPlayer) {
    try {
      if (window.currentPlayer.destroy) {
        window.currentPlayer.destroy();
      }
    } catch (e) {
      console.warn('Failed to destroy player:', e);
    }
    window.currentPlayer = null;
  }

  if (window.playerEscHandler) {
    document.removeEventListener('keydown', window.playerEscHandler);
    window.playerEscHandler = null;
  }
}
